import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Locale, translations, TranslationSchema } from '../locales';

export const LANGUAGE_STORAGE_KEY = 'qprice.lang';
export const DEFAULT_LOCALE: Locale = 'ru';

export const EVENT_LOCALE_CHANGED = 'qprice:locale_changed';

/**
 * Синхронно возвращает активный язык для сетевого слоя (axios interceptor).
 */
export function getActiveLanguage(): Locale {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'ru' || stored === 'kk' || stored === 'en') {
      return stored;
    }
  } catch {
    // localStorage недоступен (приватный режим и т.п.)
  }
  return DEFAULT_LOCALE;
}

interface LanguageContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  strings: TranslationSchema;
  t: TranslationSchema;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function detectInitialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'ru' || stored === 'kk' || stored === 'en') {
      return stored;
    }
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE;
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    if (next !== 'ru' && next !== 'kk' && next !== 'en') return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent(EVENT_LOCALE_CHANGED, { detail: next }));
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Синхронизация между вкладками браузера
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        const next = event.newValue;
        if (next === 'ru' || next === 'kk' || next === 'en') {
          setLocaleState(next);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const strings = useMemo(() => translations[locale] || translations[DEFAULT_LOCALE], [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      setLocale,
      strings,
      t: strings,
    }),
    [locale, setLocale, strings],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

const DEFAULT_CONTEXT_VALUE: LanguageContextValue = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  strings: translations[DEFAULT_LOCALE],
  t: translations[DEFAULT_LOCALE],
};

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  return ctx || DEFAULT_CONTEXT_VALUE;
}
